import React from 'react';
import { LEGAL_DOCS } from '../../../config/legal';
import type { SignupConsentHandler, SignupWizardData, SignupWizardErrors } from './types';

// Same link treatment as the cookie banner, so every consent surface matches.
const LEGAL_LINK_CLASS =
  'underline underline-offset-2 transition-colors duration-fast hover:text-primary-600 dark:hover:text-primary-300 focus-ring rounded-sm';

const CHECKBOX_CLASS = 'mt-0.5 h-4 w-4 accent-primary-500 focus-ring';

/** Fallback when step 2's legal name is somehow empty — never an empty blank. */
const FALLBACK_ORGANIZATION = 'your organization';

/**
 * Terminates the organization name with a single period.
 *
 * Legal-entity suffixes routinely end in one ('Acme Learning S.L.'), and
 * appending another rendered 'S.L.. I confirm'. `legal_service.py`'s
 * `build_acceptance_statement` applies this identical rule, so the sentence
 * shown beside the checkbox stays identical to the one persisted as proof.
 */
export const terminateOrganization = (organization: string): string =>
  organization.endsWith('.') ? organization : `${organization}.`;

/**
 * The exact sentence the user agrees to, mirroring
 * `legal_service.ACCEPTANCE_STATEMENT_TEMPLATE`. It is the checkbox's
 * accessible name, so what a screen-reader user hears is what gets persisted
 * as proof.
 */
export const buildAcceptanceStatement = (organization: string): string =>
  `I have read and accept the Terms & Conditions and the Privacy Policy, including its ` +
  `data-processing Annex, on behalf of ${terminateOrganization(organization)} I confirm I am ` +
  `of legal age and have the authority to bind this organization.`;

/** Copy of the optional consent, mirroring `legal_service.MARKETING_STATEMENT`. */
export const MARKETING_STATEMENT = 'Send me product news and learning content.';

interface StepLegalProps {
  data: SignupWizardData;
  errors: SignupWizardErrors;
  disabled: boolean;
  onConsentChange: SignupConsentHandler;
}

/**
 * Step 3 of the sign-up wizard: the legal checkpoint.
 *
 * Two independent decisions, never bundled: the required contract acceptance,
 * then — below a rule, in its own block — the optional marketing consent.
 * Both start un-ticked; the host keeps the submit button disabled until the
 * required one is ticked.
 */
const StepLegal: React.FC<StepLegalProps> = ({ data, errors, disabled, onConsentChange }) => {
  const organization = data.companyLegalName.trim() || FALLBACK_ORGANIZATION;

  return (
    <div>
      {/* Required: acceptance of the contract. Links inside a <label> are safe —
          the label's activation behaviour does nothing for events targeted at
          interactive descendants, so opening a policy never ticks the box. */}
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          name="acceptTerms"
          checked={data.acceptTerms}
          onChange={(event) => onConsentChange('acceptTerms', event.target.checked)}
          disabled={disabled}
          required
          className={CHECKBOX_CLASS}
          aria-label={buildAcceptanceStatement(organization)}
          aria-invalid={errors.acceptTerms ? true : undefined}
          aria-describedby={errors.acceptTerms ? 'acceptTerms-error' : undefined}
        />
        <span className="text-sm leading-relaxed text-text-secondary">
          I have read and accept the{' '}
          <a
            href={LEGAL_DOCS.termsAndConditions}
            target="_blank"
            rel="noreferrer noopener"
            className={LEGAL_LINK_CLASS}
          >
            Terms &amp; Conditions
          </a>{' '}
          and the{' '}
          <a
            href={LEGAL_DOCS.privacyPolicy}
            target="_blank"
            rel="noreferrer noopener"
            className={LEGAL_LINK_CLASS}
          >
            Privacy Policy
          </a>
          , including its data-processing Annex, on behalf of{' '}
          <span className="font-semibold text-text-primary">
            {terminateOrganization(organization)}
          </span>{' '}
          I confirm I am of legal age and have the authority to bind this organization.
        </span>
      </label>
      {errors.acceptTerms && (
        <p id="acceptTerms-error" role="alert" className="mt-2 text-xs text-error-500">
          {errors.acceptTerms}
        </p>
      )}

      {/* Optional: marketing. Its own block behind a rule so consent is never
          bundled with the contract acceptance above. */}
      <div className="mt-6 border-t border-border-primary/20 pt-6">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            name="acceptMarketing"
            checked={data.acceptMarketing}
            onChange={(event) => onConsentChange('acceptMarketing', event.target.checked)}
            disabled={disabled}
            className={CHECKBOX_CLASS}
            aria-label={MARKETING_STATEMENT}
            aria-describedby="acceptMarketing-hint"
          />
          <span className="text-sm leading-relaxed text-text-secondary">{MARKETING_STATEMENT}</span>
        </label>
        <p id="acceptMarketing-hint" className="mt-2 pl-7 text-xs text-text-tertiary">
          Optional. Leaving this unticked does not affect your account.
        </p>
      </div>
    </div>
  );
};

export default StepLegal;
